import { create } from 'zustand';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../config/api';

interface User {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: 'RIDER' | 'DRIVER' | 'ADMIN';
  profile_image?: string;
  is_verified: boolean;
  email_verified_at?: string;
  phone_verified_at?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  registerDriver: (data: any) => Promise<void>;
  logout: () => Promise<void>;
  loadUser: () => Promise<void>;
  updateUser: (user: Partial<User>) => void;
  uploadProfilePicture: (file: File) => Promise<any>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,

  login: async (email: string, password: string) => {
    try {
      const response: any = await apiService.post<{
        user: User;
        access_token: string;
        refresh_token: string;
      }>(API_ENDPOINTS.AUTH.LOGIN, {
        identifier: email,
        password,
      });

      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.user));

      set({
        user: response.user,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        isAuthenticated: true,
      });
    } catch (error: any) {
      throw new Error(error.message || 'Login failed');
    }
  },

  registerDriver: async (data: any) => {
    try {
      const { role, password_confirmation, ...registrationData } = data;



      const payload: any = { ...registrationData };


      if (import.meta.env.DEV) {
        console.log('📝 Registering driver with data:', {
          ...payload,
          password: '***',
          password_confirmation: '***',
        });
      }

      const response: any = await apiService.post<{
        user: User;
        access_token: string;
        refresh_token: string;
      }>(API_ENDPOINTS.AUTH.REGISTER_DRIVER, payload);

      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.user));

      set({
        user: response.user,
        accessToken: response.access_token,
        refreshToken: response.refresh_token,
        isAuthenticated: true,
      });
    } catch (error: any) {
      throw new Error(error.message || 'Driver registration failed');
    }
  },

  logout: async () => {
    try {

      try {
        const { wsService } = await import('../services/websocket');
        if (wsService && wsService.isConnected && wsService.isConnected()) {
          wsService.disconnect();
        }
      } catch (wsError) {
        console.error('WebSocket disconnect error:', wsError);
      }


      try {
        await apiService.post(API_ENDPOINTS.AUTH.LOGOUT);
      } catch (error: any) {

        if (import.meta.env.DEV) {
          console.log('Logout API error (continuing anyway):', error?.response?.status || error?.message);
        }
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {

      try {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      } catch (storageError) {
        console.error('Storage clear error:', storageError);
      }


      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
      });
    }
  },

  loadUser: async () => {
    try {
      if (import.meta.env.DEV) {
        console.log('Loading user from localStorage...');
      }
      const accessToken = localStorage.getItem('access_token');
      const userStr = localStorage.getItem('user');

      if (import.meta.env.DEV) {
        console.log('localStorage values:', { accessToken: !!accessToken, userStr: !!userStr });
      }

      if (accessToken && userStr) {
        const user = JSON.parse(userStr);



        try {

          await apiService.get(API_ENDPOINTS.USERS.PROFILE);

          if (import.meta.env.DEV) {
            console.log('Token validated, setting authenticated state');
          }
          set({
            user,
            accessToken,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: any) {

          if (import.meta.env.DEV) {
            console.log('Token validation failed, clearing storage:', error?.response?.status || error?.message);
          }


          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');

          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      } else {
        if (import.meta.env.DEV) {
          console.log('No user found, setting isLoading to false');
        }
        set({ 
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false 
        });
      }
    } catch (error) {
      console.error('Load user error:', error);

      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      set({ 
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: false 
      });
    }
  },

  updateUser: (user: Partial<User>) => {
    const currentUser = get().user;
    if (currentUser) {
      const updatedUser = { ...currentUser, ...user };
      set({ user: updatedUser });
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  },

  uploadProfilePicture: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      if (import.meta.env.DEV) {
        console.log('📤 Uploading profile picture:', {
          fileName: file.name,
          fileSize: `${(file.size / 1024).toFixed(2)} KB`,
          fileType: file.type,
        });
      }

      const response: any = await apiService.upload(
        API_ENDPOINTS.USERS.UPLOAD_PROFILE_PICTURE,
        formData
      );

      if (import.meta.env.DEV) {
        console.log('✅ Profile picture uploaded successfully:', response);
      }


      const updatedUser: any = await apiService.get(API_ENDPOINTS.USERS.PROFILE);


      const mappedUser: User = {
        ...updatedUser,
        profile_image: updatedUser.profile_image_url || updatedUser.profile_image || response.url,
      };

      set({ user: mappedUser });
      localStorage.setItem('user', JSON.stringify(mappedUser));

      return response;
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ Profile picture upload error:', error);
      }
      throw new Error(error.message || 'Failed to upload profile picture');
    }
  },
}));

